import { ProcessType } from './request';
import { OperationDescriptor } from './processing-pipeline/operation.model';
import { JitToolDefinition, JIT_TOOLS, TOOL_GROUPS, ToolGroupId } from './jit-tool-registry';

// ── Lookup Maps (computed once at module load) ────────────────────────────────

export const TOOL_BY_OPERATION_ID: ReadonlyMap<string, JitToolDefinition> =
  new Map(JIT_TOOLS.map(t => [t.operationId, t]));

export const TOOL_BY_PROCESS_TYPE: ReadonlyMap<ProcessType, JitToolDefinition> =
  new Map(JIT_TOOLS.map(t => [t.processType, t]));

/** Reverse of `serverProcessType` (e.g. SIFT → the ALIGNMENT tool) - a Rule
 *  stores whatever wire ProcessType the request was actually submitted
 *  under, so looking it up in `TOOL_BY_PROCESS_TYPE` alone misses tools
 *  whose client-facing type differs from their wire type. */
export const TOOL_BY_SERVER_PROCESS_TYPE: ReadonlyMap<ProcessType, JitToolDefinition> =
  new Map(JIT_TOOLS.filter(t => t.serverProcessType != null).map(t => [t.serverProcessType as ProcessType, t]));

/** Resolves a ProcessType coming back from the server (e.g. a Rule's
 *  `processType`) to its tool definition, accounting for tools whose wire
 *  type differs from their client type - see `TOOL_BY_SERVER_PROCESS_TYPE`. */
export function getToolByServerProcessType(processType: ProcessType): JitToolDefinition | undefined {
  return TOOL_BY_PROCESS_TYPE.get(processType) ?? TOOL_BY_SERVER_PROCESS_TYPE.get(processType);
}

export const OPERATION_TO_PROCESS_TYPE: ReadonlyMap<string, ProcessType> =
  new Map(JIT_TOOLS.map(t => [t.operationId, t.processType]));

// ── Pipeline Adapter: JitToolDefinition → OperationDescriptor ─────────────────

function toOperationDescriptor(tool: JitToolDefinition): OperationDescriptor {
  return {
    id: tool.operationId,
    name: tool.displayName,
    engineId: 'server',
    category: tool.pipelineCategory!,
    description: tool.description,
    // Tools with a local preview engine (e.g. cellpose-sam) actually run in the
    // browser on step-select, so they're both 'client' and 'server' capable.
    runsOn: tool.previewEngineId ? 'both' : 'server',
    params: tool.pipelineParams || [],
    requiresManualRun: tool.requiresManualRun,
  };
}

export function getAllServerOperations(): OperationDescriptor[] {
  return JIT_TOOLS
    .filter(t => t.pipelineCategory != null)
    .map(toOperationDescriptor);
}

/** Returns the ProcessType used for processService.run(), respecting serverProcessType overrides. */
export function getServerProcessType(tool: JitToolDefinition): ProcessType {
  return tool.serverProcessType ?? tool.processType;
}

// ── Process Dropdown Adapter ──────────────────────────────────────────────────

export interface IProcessType {
  name: string;
  desc: string;
  type: ProcessType;
  icon: string;
}

export interface IProcessGroup {
  label: string;
  icon: string;
  items: IProcessType[];
}

export function getProcessDropdownGroups(): IProcessGroup[] {
  const groupMap = new Map<ToolGroupId, IProcessType[]>();
  for (const tool of JIT_TOOLS) {
    if (tool.hiddenFromProcessDropdown) continue;
    const items = groupMap.get(tool.groupId) || [];
    items.push({
      name: tool.displayName,
      desc: tool.description,
      type: tool.processType,
      icon: tool.icon,
    });
    groupMap.set(tool.groupId, items);
  }
  return TOOL_GROUPS
    .filter(g => groupMap.has(g.id))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(g => ({ label: g.label, icon: g.icon, items: groupMap.get(g.id)! }));
}

export function filterByActiveTools(
  groups: IProcessGroup[],
  activeToolNames: string[]
): IProcessGroup[] {
  return groups
    .map(g => ({
      ...g,
      items: g.items.filter(tool => activeToolNames.includes(String(tool.type))),
    }))
    .filter(g => g.items.length > 0);
}
